'use client';
import Script from 'next/script';
import dynamic from 'next/dynamic';

import { TCatalog } from '@/actions/catalogs/types';
import { VillagesData } from '@/actions/villages';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/AccordionCN';
import { PaginationFooter } from '@/components/VillagesWrapper/PaginationFooter';
import Paragraph from '@/components/VillagesWrapper/Paragraph';
import useFilterContext from '@/hooks/useFilterContext';
import useGlobalContext from '@/hooks/useGlobalContext';
import { useLocale } from '@/hooks/useLocale';
import { cn } from '@/lib/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { parseDateString } from '@/lib/rome-date';
import { useSearchParams } from 'next/navigation';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loading } from '../Loading';
import { VillageCardDictonary } from '../VillageCard/Root';
import VillagesHeader from '../VillagesHeader';
import { VillagesHeaderDicts } from '../VillagesHeader/Filter';
import schema, { FormValues } from '../VillagesHeader/schema';
import VillagesList from './List';

// Rendered only once the map is opened: Google Maps and the map carousel load on that click
const MapContainer = dynamic(
  () =>
    import('@/components/VillagesWrapper/MapContainer').then(
      (mod) => mod.MapContainer,
    ),
  {
    ssr: false,
    // Until the chunk arrives (the container shows its own loader after that)
    loading: () => (
      <div className="w-full my-4 flex items-center justify-center">
        <Loading />
      </div>
    ),
  },
);

export type SearchParams = {
  currency?: string;
  catalogId?: string;
  region?: number;
  searchParams: { [key: string]: string | undefined };
  page: number;
};

type Props = {
  lang: string;
  villagesData: VillagesData;
  dicts: VillagesHeaderDicts & VillageCardDictonary;
  headerTitle: string;
  catalog?: TCatalog;
  headerPaths: {
    label: string;
    path?: string | undefined;
  }[];
  params: SearchParams;
  currency?: string;
} & PropsWithChildren;

export default function VillagesWrapper({
  lang,
  villagesData,
  headerTitle,
  catalog,
  headerPaths,
  dicts,
  children,
  params,
  currency,
}: Props) {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const locale = useLocale();

  const searchParams = useSearchParams();
  const adultParam = searchParams.get('adults');
  const childrenParam = searchParams.get('children');
  const infantsParam = searchParams.get('infants');
  const startDateParam = searchParams.get('startDate');
  const endDateParam = searchParams.get('endDate');

  const { quantities, setQuantities, selectedDates, setSelectedDates } =
    useFilterContext();
  const { setIsDynamicUrl, setDynamicUrl } = useGlobalContext();

  const {
    villages: villagesLabel,
    singularSearchResult,
    pluralSearchResult,
    singularNearSearchResult,
    pluralNearSearchResult,
  } = dicts.contentDict;

  const hasNearCoordinates =
    !!params.searchParams.latitude && !!params.searchParams.longitude;
  const activeSingularSearchResult = hasNearCoordinates
    ? singularNearSearchResult
    : singularSearchResult;
  const activePluralSearchResult = hasNearCoordinates
    ? pluralNearSearchResult
    : pluralSearchResult;
  const searchResultLabel =
    villagesData.count > 1
      ? activePluralSearchResult
      : activeSingularSearchResult;

  const searchResult = params.searchParams.q || params.searchParams.city;

  const formQuantities = getFormQuantities();
  const formDates = getFormDates();

  const contents = catalog?.contents[locale] ?? '';

  function onSwitch(mapVisibility: boolean) {
    setShowMap(mapVisibility);
  }

  const filterForm = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      dates: formDates,
      quantities: formQuantities,
      destination: dicts.destinationsDict[0],
    },
  });

  function getFormDates() {
    if (startDateParam && endDateParam) {
      return {
        start: parseDateString(startDateParam),
        end: parseDateString(endDateParam),
      };
    }

    return {
      start: null,
      end: null,
    };
  }

  function getFormQuantities() {
    let adults = 0;
    let infants = 0;
    let children = 0;
    if (infantsParam) {
      infants = parseInt(infantsParam);
    }
    if (adultParam) {
      adults = parseInt(adultParam);
    }
    if (childrenParam) {
      children = parseInt(childrenParam);
    }

    return {
      adults,
      infants,
      children,
    };
  }

  useEffect(() => {
    const catalogSlugs =
      catalog &&
      catalog.translate.reduce(
        (acc: Record<string, string>, { locale, slug }) => {
          let path;
          switch (locale) {
            case 'it':
              path = `/it/sicilia/${slug}`;
              break;
            case 'de':
              path = `/de/sizilien/${slug}`;
              break;
            case 'fr':
              path = `/fr/sicile/${slug}`;
              break;
            default:
              path = `/sicily/${slug}`;
          }

          acc[locale] = path;
          return acc;
        },
        {},
      );

    setIsDynamicUrl(!!catalog);
    setDynamicUrl(catalogSlugs);

    return () => {
      setIsDynamicUrl(false);
      setDynamicUrl({
        it: '',
        en: '',
        de: '',
        fr: '',
      });
    };
    // eslint-disable-next-line
  }, []);

  // The map sticks once the page scrolls past the catalogs row (measured on mount)
  useEffect(() => {
    const stickyFrom = document
      .querySelector('#villages-catalogs')
      ?.getBoundingClientRect().top;
    if (!stickyFrom) return;

    const onScroll = () => {
      document
        .querySelector('#map-root')
        ?.classList.toggle('is-sticky', window.scrollY >= stickyFrom);
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const formQuantities = getFormQuantities();
    const hasQuantityFromUrl = Object.values(formQuantities).some(
      (quantity) => quantity > 0,
    );

    if (startDateParam && endDateParam) {
      setSelectedDates({
        start: parseDateString(startDateParam),
        end: parseDateString(endDateParam),
      });
    } else {
      if (selectedDates) {
        setSelectedDates(undefined);
      }
    }

    if (hasQuantityFromUrl) {
      setQuantities(formQuantities);
    }
    if (quantities && !hasQuantityFromUrl) {
      setQuantities(undefined);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <VillagesHeader.Root
        title={searchResult ? villagesLabel : headerTitle}
        paths={headerPaths}>
        <VillagesHeader.Mobile
          form={filterForm}
          modalIsOpen={modalIsOpen}
          setModalIsOpen={setModalIsOpen}
          lang={lang}
          dicts={dicts}
        />
        <VillagesHeader.Filter
          form={filterForm}
          lang={lang}
          className="hidden desktop:flex"
          dicts={dicts}>
          <VillagesHeader.Switch onSwitch={onSwitch} />
        </VillagesHeader.Filter>
      </VillagesHeader.Root>
      <div className="w-full flex flex-col px-sm md:px-md desktop:px-lg">
        <div
          id="header-ghost"
          className="hidden mb-[196px] [&.active]:desktop:block"
        />
        {children}
        {searchResult && (
          <h3 className="text-medium mb-2 font-title">
            {villagesData.count} {searchResultLabel} {searchResult}
          </h3>
        )}
        <>
          <div className={cn('flex gap-6', { hidden: !showMap })}>
            {/* Mounted only when opened: it loads every page of villas */}
            {showMap && (
              <MapContainer
                dicts={{
                  regionZoneDict: dicts.regionZoneDict,
                  currencyDict: dicts.currencyDict,
                  villageDict: dicts.villageDict,
                }}
                currency={currency}
                villagesCount={villagesData.count}
                searchParams={params}
              />
            )}
          </div>
          <div className={cn('flex flex-col gap-6', { hidden: showMap })}>
            <VillagesList
              dicts={{
                regionZoneDict: dicts.regionZoneDict,
                currencyDict: dicts.currencyDict,
                villageDict: dicts.villageDict,
              }}
              villages={villagesData.data}
              showMap={false}
              currency={currency}
            />

            <PaginationFooter
              villagesData={villagesData}
              currentPage={params.page}
            />

            <div>
              <Paragraph
                content={contents}
                className={cn(
                  'paragraph-content', // link styles are on globals.css
                  'mb-8',
                  '[&>h2]:text-2xl [&>h2]:font-semibold [&>h2]:mb-4',
                  '[&>h3]:text-xl [&>h3]:font-semibold [&>h3]:mb-4',
                  '[&>p]:mb-4 [&>p]:leading-[1.6] [&>p]:lg:max-w-[60vw]',
                  '[&>ul]:list-disc [&>ul]:pl-8 [&>ul]:mb-4',
                )}
              />
            </div>
          </div>
        </>
      </div>

      <div className={cn({ hidden: showMap })}>
        <CatalogFaqs faqs={catalog?.faqs} />
      </div>
    </>
  );
}

function CatalogFaqs({ faqs }: { faqs?: TCatalog['faqs'] }) {
  const locale = useLocale();

  const localeFaqs = faqs?.[locale];

  if (!localeFaqs || localeFaqs.length === 0) {
    return null;
  }

  const allFaqs = Object.values(faqs).flat();

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: allFaqs.map(({ title, content }) => {
      return {
        '@type': 'Question',
        name: title,
        acceptedAnswer: {
          '@type': 'Answer',
          text: content,
        },
      };
    }),
  };

  return (
    <div className="w-full flex flex-col px-sm md:px-md desktop:px-lg mt-12 py-12 bg-gray-100">
      <h2 className="text-3xl font-title mb-4 text-center">
        <span className="styled-link">FAQ</span>
      </h2>
      <Accordion
        type="single"
        collapsible
        defaultValue={localeFaqs[0].id.toString()}
        className="grid md:grid-cols-2 gap-8">
        {localeFaqs.map((faq) => (
          <AccordionItem value={faq.id.toString()} key={faq.id}>
            <AccordionTrigger className="text-xl hover:no-underline">
              {faq.title}
            </AccordionTrigger>
            <AccordionContent className="text-base opacity-80 space-y-2">
              {faq.content.split('\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <Script
        id="catalog-faq-json-ld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </div>
  );
}
